import { CircleCheck, CircleDashed, CircleSlash, Target } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { GoalTier } from "@/lib/api";
import { goalLabel } from "@/lib/format";
import { cn } from "@/lib/utils";

const STYLES: Record<GoalTier, { className: string; Icon: typeof Target }> = {
  perfect: {
    className:
      "border-emerald-600/30 bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
    Icon: CircleCheck,
  },
  partial: {
    className:
      "border-amber-600/30 bg-amber-500/12 text-amber-700 dark:text-amber-300",
    Icon: Target,
  },
  failed: {
    className:
      "border-rose-600/30 bg-rose-500/12 text-rose-700 dark:text-rose-300",
    Icon: CircleSlash,
  },
};

const UNSCORED = {
  className: "border-muted-foreground/25 text-muted-foreground",
  Icon: CircleDashed,
};

/**
 * Tier chip shown on the leaderboard and on submission rows.
 *
 * `tier` is null for a submission that never produced a score — a rejected
 * recording, or one still being judged. That is rendered as its own neutral state
 * rather than falling through to "Missed": a maze mismatch is not a bad attempt,
 * and telling a student it was would send them optimising a run that was never
 * evaluated.
 */
export function GoalBadge({
  tier,
  className,
}: {
  tier: GoalTier | null;
  className?: string;
}) {
  const style = tier === null ? UNSCORED : STYLES[tier];
  const { Icon } = style;
  return (
    <Badge
      variant="outline"
      className={cn("gap-1 font-medium", style.className, className)}
    >
      <Icon className="size-3" aria-hidden />
      {tier === null ? "Not scored" : goalLabel(tier)}
    </Badge>
  );
}
